import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import api, { isAPIError } from 'containers/api'
import useETLWebSocket from './useETLWebSocket'
import useTriggerETL from './useTriggerETL'
import useGetETLlogs from './useGetETLlogs'
import { IOrganization, ETLProgressUpdate, ETLCurrentRun, ETLRunSteps, IETLlogs } from 'types'

interface ETLContextValue {
  // Real-time progress from WebSocket
  liveProgress: ETLProgressUpdate | null
  wsConnected: boolean
  wsError: string | null

  // Current run details (supports multiple concurrent runs)
  currentRuns: ETLCurrentRun[]
  activeRunCount: number
  currentRunLoading: boolean
  currentRunError: string | null
  refreshCurrentRun: () => Promise<void>

  // Historical logs
  etlLogs: IETLlogs | undefined
  latestETLDate: Date | undefined
  loadingLogs: boolean
  errorGettingLogs: string
  getETLlogs: () => Promise<void>
  hasMoreLogs: boolean
  loadMoreLogs: () => void
  loadingMoreLogs: boolean

  // Run steps detail
  runSteps: ETLRunSteps | null
  runStepsLoading: boolean
  runStepsError: string | null
  getRunSteps: (runId: string) => Promise<void>

  // Trigger ETL
  triggerETL: (args?: string | null) => Promise<void>
  triggerLoading: boolean
}

const ETLContext = createContext<ETLContextValue | undefined>(undefined)

interface ETLProviderProps {
  organization: IOrganization
  children: ReactNode
  enableWebSocket?: boolean
}

export const ETLProvider: React.FC<ETLProviderProps> = ({
  organization,
  children,
  enableWebSocket = true,
}) => {
  const token = useSelector(selectToken)

  // WebSocket for real-time updates
  const { progress: liveProgress, connected: wsConnected, error: wsError } = useETLWebSocket({
    organizationId: organization.id,
    token: token?.accessToken || '',
    enabled: enableWebSocket && !!token?.accessToken,
  })

  // Historical logs
  const {
    getETLlogs,
    etlLogs,
    latestETLDate,
    loadingLogs,
    errorGettingLogs,
    hasMore: hasMoreLogs,
    loadMoreLogs,
    loadingMore: loadingMoreLogs,
  } = useGetETLlogs(organization)

  // Trigger ETL
  const [triggerMutator, triggerStatus] = useTriggerETL()
  const triggerLoading = triggerStatus === 'pending'

  // Current run state (supports multiple concurrent runs)
  const [currentRuns, setCurrentRuns] = useState<ETLCurrentRun[]>([])
  const [activeRunCount, setActiveRunCount] = useState(0)
  const [currentRunLoading, setCurrentRunLoading] = useState(false)
  const [currentRunError, setCurrentRunError] = useState<string | null>(null)

  // Run steps state
  const [runSteps, setRunSteps] = useState<ETLRunSteps | null>(null)
  const [runStepsLoading, setRunStepsLoading] = useState(false)
  const [runStepsError, setRunStepsError] = useState<string | null>(null)

  const refreshCurrentRun = async () => {
    try {
      setCurrentRunLoading(true)
      setCurrentRunError(null)
      const run = await api.getCurrentETLRun({
        organizationId: organization.id,
        token,
      })

      // Handle both single run and multiple runs response formats
      if (Array.isArray(run)) {
        setCurrentRuns(run)
        setActiveRunCount(run.length)
      } else if (run) {
        setCurrentRuns([run])
        setActiveRunCount(1)
      } else {
        setCurrentRuns([])
        setActiveRunCount(0)
      }
    } catch (err) {
      if (isAPIError(err) && err.response?.status === 404) {
        // No current run, this is expected when ETL is not running
        setCurrentRuns([])
        setActiveRunCount(0)
      } else if (isAPIError(err) && typeof err.response?.data?.detail === 'string') {
        setCurrentRunError(err.response.data.detail)
      } else {
        setCurrentRunError('Failed to fetch current ETL run')
      }
    } finally {
      setCurrentRunLoading(false)
    }
  }

  const getRunSteps = async (runId: string) => {
    try {
      setRunStepsLoading(true)
      setRunStepsError(null)
      const steps = await api.getETLRunSteps({
        organizationId: organization.id,
        runId,
        token,
      })
      setRunSteps(steps)
    } catch (err) {
      if (isAPIError(err) && typeof err.response?.data?.detail === 'string') {
        setRunStepsError(err.response.data.detail)
      } else {
        setRunStepsError('Failed to fetch ETL run steps')
      }
    } finally {
      setRunStepsLoading(false)
    }
  }

  const triggerETL = async (args?: string | null) => {
    // triggerMutator expects the organizationId as the payload since that's what the inner function expects
    await triggerMutator(organization.id)
    // Refresh current run after triggering
    setTimeout(() => refreshCurrentRun(), 1000)
  }

  // Fetch current run on mount and when live progress changes running state
  useEffect(() => {
    if (liveProgress?.running) {
      refreshCurrentRun()
    } else if (liveProgress?.running === false) {
      // ETL finished — clear current runs and refresh logs
      setCurrentRuns([])
      setActiveRunCount(0)
      getETLlogs()
    }
  }, [liveProgress?.running])

  // Poll cleric when activeRunCount > 0 but no WebSocket progress is arriving.
  // Handles fast-fail ETL runs that finish before writing any progress updates.
  useEffect(() => {
    if (activeRunCount > 0 && !liveProgress?.running) {
      const interval = setInterval(async () => {
        try {
          const run = await api.getCurrentETLRun({
            organizationId: organization.id,
            token,
          })
          if (!run || (isAPIError(run) && (run as any).response?.status === 404)) {
            // Run finished without progress updates
            setCurrentRuns([])
            setActiveRunCount(0)
            getETLlogs()
          }
        } catch (err) {
          if (isAPIError(err) && err.response?.status === 404) {
            setCurrentRuns([])
            setActiveRunCount(0)
            getETLlogs()
          }
        }
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [activeRunCount, liveProgress?.running])

  // Initial load of historical logs
  useEffect(() => {
    getETLlogs()
  }, [organization.id])

  // Seed current-run state on mount so a run already in flight before this provider
  // mounted (e.g. one kicked server-side on activation) is visible immediately, without
  // waiting for the first WebSocket progress message — which may never arrive if the run
  // finished before the socket connected.
  useEffect(() => {
    refreshCurrentRun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id])

  const value: ETLContextValue = {
    // Real-time
    liveProgress,
    wsConnected,
    wsError,

    // Current runs (supports multiple concurrent runs)
    currentRuns,
    activeRunCount,
    currentRunLoading,
    currentRunError,
    refreshCurrentRun,

    // Historical logs
    etlLogs,
    latestETLDate,
    loadingLogs,
    errorGettingLogs,
    getETLlogs,
    hasMoreLogs,
    loadMoreLogs,
    loadingMoreLogs,

    // Run steps
    runSteps,
    runStepsLoading,
    runStepsError,
    getRunSteps,

    // Trigger
    triggerETL,
    triggerLoading,
  }

  return <ETLContext.Provider value={value}>{children}</ETLContext.Provider>
}

export const useETLContext = () => {
  const context = useContext(ETLContext)
  if (context === undefined) {
    throw new Error('useETLContext must be used within an ETLProvider')
  }
  return context
}
